import React, {useCallback, useMemo} from 'react'
import {useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {useShowTotalPriceGhaOrGvrPreferredRate} from 'components/Offer/hooks/useShowTotalPriceGhaOrGvrPreferredRate'
import {GHAPreferredRateTotalPrice} from 'components/Offer/Shared/OfferPrice/GHAPreferredRateTotalPrice'
import {TaxationAndPriceMessage} from 'components/TaxationAndPriceMessage'
import {useOtaMode} from 'hooks/useOtaMode'
import {usePriceUtils} from 'hooks/usePriceUtils'
import {useVioTag} from 'hooks/useVioTag'
import {getCurrencyCode} from 'modules/meta/selectors'
import {
  getLengthOfStay,
  getNumberOfRooms,
  getOfferAccessFlowSettings,
  getShowTotalPrices,
  getShowTotalPriceToggle
} from 'modules/sapiSearch/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'
import {OfferPrimaryLayoutVariant} from '@vio/core/src/offer/types/OfferLayout'
import {OfferVariant} from '@vio/core/src/offer/types/OfferVariant'
import {SearchOffer} from '@vio/core/src/offer/types/SearchOffer'
import {PriceSize} from '@vio/shared/src/search/Price'

import {OfferPrice} from '../../Shared/OfferPrice'

interface MainProps {
  hotelId: string
  anchorPrice: number
  offer: SearchOffer
  offerVariant?: OfferVariant
  anchorPriceTotal?: number
  shouldShowTaxationAndNightly?: boolean
  variant: OfferPrimaryLayoutVariant
  showPriceIndication?: boolean
  shouldShowTaxation?: boolean
  isSplitBooking?: false
  forceMobileLayout?: boolean
}

interface SplitBookingProps {
  isSplitBooking: boolean
  hotelId: string
  shouldShowTaxationAndNightly?: boolean
  variant: OfferPrimaryLayoutVariant
  shouldShowTaxation?: boolean
  showPriceIndication?: never
  anchorPrice?: number
  anchorPriceTotal?: number
  offer?: never
  offerVariant?: never
  forceMobileLayout?: boolean
}

const getOfferPriceComponentLayout = (
  variant: OfferPrimaryLayoutVariant,
  isLongPrice: boolean,
  horizontally: boolean,
  shouldWrapAnchorPrice?: boolean
): 'display-row' | 'display-column' | 'default' => {
  if (
    !['PrimaryMultiColumn', 'PrimaryMultiColumnFullWidthCTA'].includes(variant)
  ) {
    return 'default'
  }

  if (variant === 'PrimaryMultiColumn') {
    if (shouldWrapAnchorPrice) return 'display-column'
    if (!isLongPrice) return 'display-row'
    return 'display-column'
  }

  if (variant === 'PrimaryMultiColumnFullWidthCTA') {
    if (shouldWrapAnchorPrice) return 'display-column'
    if (!isLongPrice && horizontally) return 'display-row'
    return 'display-column'
  }

  return 'default'
}

// The leading '&&' increases specificity of the generated CSS selector.
// In styled-components, '&&' ensures these styles override conflicting styles from the base OfferPrice component or other classNames.
//  TODO: @(Search) - Let's re-think this styling, since it's not part of our conventions

const OfferPriceComponent = styled(OfferPrice)`
  &&.--display-row {
    flex-wrap: nowrap;
    flex-direction: row;
    align-items: baseline;
    gap: ${cssTheme.layout.spacing.s200};
    padding: 0;
  }

  &&.--display-column {
    flex-wrap: wrap;
    flex-direction: column;
    align-items: flex-end;
    gap: 0;
    padding: 0;
  }

  &.--default {
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    padding: 0 ${cssTheme.layout.spacing.s100};

    &.--long-price {
      padding: 0 0;
    }
  }

  &.--variant-PrimaryMultiColumnFullWidthCTA {
    padding: 0 ${cssTheme.layout.spacing.s200};
    &.--long-price {
      padding: 0 0;
    }
  }
`

export const PriceComponent: React.FC<MainProps | SplitBookingProps> = ({
  isSplitBooking,
  hotelId,
  anchorPrice = 0,
  offer,
  offerVariant,
  anchorPriceTotal = 0,
  shouldShowTaxation = true,
  shouldShowTaxationAndNightly,
  variant = 'PrimaryDefault',
  showPriceIndication = false,
  forceMobileLayout
}) => {
  const {isMobile} = useDeviceLayout()

  const {isAuthRequiredUnlocked, isAppRequired, isAppLocked, isSpecial} =
    useSelector(state => getOfferAccessFlowSettings(state, hotelId, offer?.id))

  const showTotalPrices = useSelector(getShowTotalPrices)
  const showTotalPriceToggle = useSelector(getShowTotalPriceToggle)
  const currencyCode = useSelector(getCurrencyCode)
  const {getPriceDetails} = usePriceUtils(forceMobileLayout)
  const {isLongPrice, shouldWrapAnchorPrice} = getPriceDetails(
    showTotalPrices ? offer?.totalRate : offer?.nightlyRate,
    isMobile
  )
  const {isOTAMode} = useOtaMode()

  const shouldShowGhaOrGvrPreferredRateTotalPrice =
    useShowTotalPriceGhaOrGvrPreferredRate(offer, hotelId)

  // Added as part of 32cf4c25-srp-room-count-copy experiment
  const numberOfRooms = useSelector(state => getNumberOfRooms(state, hotelId))
  const numberOfNights = useSelector(getLengthOfStay)

  let priceSize: PriceSize = 'xl'
  if (isLongPrice && !shouldWrapAnchorPrice) {
    priceSize = 'md'
  }

  // This size is based on the design for OTA mode, the layout is also known as BoVio First.
  if (isOTAMode) {
    priceSize = 'lg'
  }

  const gridAlignment = useMemo<'flex-start' | 'flex-end'>(() => {
    if (variant === 'PrimaryDefault') return 'flex-start'
    return 'flex-end'
  }, [variant])

  const gridJustifyContent = useMemo<'flex-start' | 'flex-end'>(() => {
    if (variant === 'PrimaryDefault') {
      return 'flex-start'
    }

    return 'flex-end'
  }, [variant])

  const {isVioTagVisible: isVioTagEligible} = useVioTag({
    price: showTotalPrices ? offer?.totalRate : offer?.nightlyRate,
    anchorPrice: showTotalPrices ? anchorPriceTotal : anchorPrice,
    currencyCode,
    offerTags: offer?.tags
  })

  const isOnlyOnVioEligible =
    isVioTagEligible && offerVariant === OfferVariant.OnlyOnVio

  const renderTaxation = useCallback(() => {
    if (!shouldShowTaxation) return null

    return (
      <TaxationAndPriceMessage
        hotelId={hotelId}
        showPriceMessage={
          variant !== 'PrimaryDefault' && shouldShowTaxationAndNightly
        }
        isSplitBooking={isSplitBooking}
        numberOfRooms={numberOfRooms}
        numberOfNights={numberOfNights}
      />
    )
  }, [
    isSplitBooking,
    shouldShowTaxation,
    shouldShowTaxationAndNightly,
    variant,
    hotelId,
    numberOfRooms,
    numberOfNights
  ])

  const renderOfferPriceVariants = () => {
    if (!showTotalPriceToggle) {
      return (
        <OfferPriceComponent
          className={cx(
            `--variant-${variant}`,
            isLongPrice && '--long-price',
            `--${getOfferPriceComponentLayout(
              variant,
              isLongPrice,
              shouldWrapAnchorPrice
            )}`
          )}
          showRowLayout={false}
          shouldDisplayInline={false}
          offer={offer}
          isPrivateDeal={isOnlyOnVioEligible || isSpecial}
          anchorPrice={anchorPrice}
          hotelId={hotelId}
          size={priceSize}
          showPriceIndication={showPriceIndication}
          isAppLockedDeal={isAppLocked}
        />
      )
    }

    return (
      <OfferPriceComponent
        className={cx(
          `--variant-${variant}`,
          isLongPrice && '--long-price',
          `--${getOfferPriceComponentLayout(
            variant,
            isLongPrice,
            shouldWrapAnchorPrice
          )}`
        )}
        showRowLayout
        anchorPriceTotal={anchorPriceTotal}
        anchorPrice={anchorPrice}
        offer={offer}
        isPrivateDeal={isOnlyOnVioEligible || isSpecial}
        showPriceIndication={showPriceIndication}
        hotelId={hotelId}
        size={priceSize}
        isAppLockedDeal={isAppLocked}
      />
    )
  }

  if (isSplitBooking) {
    return (
      <>
        <Box
          display="flex"
          flexDirection="column"
          alignItems={gridAlignment}
          justifyContent={gridJustifyContent}
          gap="s200"
        >
          <div data-id="Price">
            <OfferPriceComponent
              className={cx(
                `--variant-${variant}`,
                isLongPrice && '--long-price',
                `--${getOfferPriceComponentLayout(
                  variant,
                  isLongPrice,
                  shouldWrapAnchorPrice
                )}`
              )}
              isSplitBooking
              hotelId={hotelId}
              showAnchorPrice
              anchorPrice={anchorPrice}
              anchorPriceTotal={anchorPriceTotal}
              showPriceIndication={
                shouldShowTaxationAndNightly && variant === 'PrimaryDefault'
              }
              size={priceSize}
              isAppLockedDeal={isAppLocked}
            />
          </div>
        </Box>
        <Box textAlign={variant === 'PrimaryDefault' ? 'start' : 'end'}>
          {renderTaxation()}
        </Box>
      </>
    )
  }

  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        alignItems={gridAlignment}
        justifyContent={gridJustifyContent}
        gap="s200"
      >
        {isAuthRequiredUnlocked && (
          <Box paddingRight="s200" marginBottom="s250" textAlign="end">
            <Text variant="labelXS" colorPath="content.brand.primary">
              <FormattedMessageWrapper
                id="offerAccessFlow.signInToBookLabel"
                defaultMessage="Sign in to book"
              />
            </Text>
          </Box>
        )}

        {isAppRequired && (
          <Box marginBottom="s200" textAlign="end">
            <Text variant="labelXS" colorPath="content.brand.primary">
              <FormattedMessageWrapper
                id="offerAccessFlow.appLocked"
                defaultMessage="Book on Vio app"
              />
            </Text>
          </Box>
        )}
        <div data-id="Price">{renderOfferPriceVariants()}</div>
      </Box>
      <Box textAlign={variant === 'PrimaryDefault' ? 'start' : 'end'}>
        {renderTaxation()}
      </Box>
      {shouldShowGhaOrGvrPreferredRateTotalPrice && (
        <GHAPreferredRateTotalPrice
          textVariant="labelXS"
          value={offer.totalRate}
          currency={currencyCode as string}
        />
      )}
    </>
  )
}
